if (!customElements.get("product-pill-slider")) {
  class ProductPillSlider extends HTMLElement {
    constructor() {
      super();

      this.desktopQuery = window.matchMedia("(min-width: 750px)");
      this.pillInputs = [];
      this.abortController = null;
      this.mutationObserver = null;
      this.dragState = null;
      this.wasDragging = false;

      this.handleModeChange = this.handleModeChange.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
    }

    connectedCallback() {
      this.pillInputs = Array.from(this.querySelectorAll(".product-form__input.product-form__input--pill"));

      if (!this.pillInputs.length) return;

      this.desktopQuery.addEventListener("change", this.handleModeChange);
      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(this, {childList: true, subtree: true});
      this.handleModeChange();
    }

    disconnectedCallback() {
      this.desktopQuery.removeEventListener("change", this.handleModeChange);
      this.mutationObserver?.disconnect();
      this.mutationObserver = null;
      this.disable();
    }

    handleModeChange() {
      if (this.desktopQuery.matches) {
        this.enable();
      } else {
        this.disable();
      }
    }

    handleMutations() {
      this.pillInputs = Array.from(this.querySelectorAll(".product-form__input.product-form__input--pill"));
      this.handleModeChange();
    }

    enable() {
      this.disable();
      this.abortController = new AbortController();

      this.pillInputs.forEach((input) => {
        this.prepareInput(input);
        this.bindInput(input);
        this.scrollCheckedIntoView(input, "auto");
      });
    }

    disable() {
      this.abortController?.abort();
      this.abortController = null;
      this.dragState = null;

      this.pillInputs.forEach((input) => {
        input.removeAttribute("tabindex");
        input.removeAttribute("role");
        input.dataset.pillSlider = "false";
      });
    }

    prepareInput(input) {
      input.dataset.pillSlider = "true";
      input.setAttribute("role", "listbox");
      input.setAttribute("tabindex", "0");
    }

    bindInput(input) {
      const options = {signal: this.abortController.signal};

      input.addEventListener("wheel", (event) => this.onWheel(event, input), options);
      input.addEventListener("pointerdown", (event) => this.onPointerDown(event, input), options);
      input.addEventListener("pointermove", (event) => this.onPointerMove(event), options);
      input.addEventListener("pointerup", () => this.onPointerUp(), options);
      input.addEventListener("pointercancel", () => this.onPointerUp(), options);
      input.addEventListener("pointerleave", () => this.onPointerUp(), options);
      input.addEventListener("click", (event) => this.onClick(event), options);
      input.addEventListener("keydown", (event) => this.onKeyDown(event, input), options);
      input.addEventListener("change", () => this.scrollCheckedIntoView(input, "smooth"), options);
    }

    onWheel(event, input) {
      if (!this.hasOverflow(input)) return;

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;

      if (!delta) return;

      input.scrollBy({
        left: delta,
        behavior: "auto",
      });
      event.preventDefault();
    }

    onPointerDown(event, input) {
      if (!this.hasOverflow(input) || event.button !== 0) return;

      this.dragState = {
        input,
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollLeft: input.scrollLeft,
        dragging: false,
      };
      this.wasDragging = false;
    }

    onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const distance = event.clientX - this.dragState.startX;

      if (Math.abs(distance) > 4) {
        this.dragState.dragging = true;
        this.wasDragging = true;
      }

      this.dragState.input.scrollLeft = this.dragState.scrollLeft - distance;
    }

    onPointerUp() {
      this.dragState = null;
    }

    onClick(event) {
      if (!this.wasDragging) return;

      event.preventDefault();
      event.stopPropagation();
      this.wasDragging = false;
    }

    onKeyDown(event, input) {
      const direction = this.getKeyboardDirection(event.key);

      if (!direction) return;

      const labels = this.getLabels(input);
      const checkedInput = input.querySelector("input[type='radio']:checked");
      const currentIndex = labels.findIndex((label) => label.htmlFor === checkedInput?.id);
      const nextIndex = Math.min(Math.max(currentIndex + direction, 0), labels.length - 1);
      const nextLabel = labels[nextIndex];

      if (!nextLabel || nextIndex === currentIndex) return;

      const nextInput = input.querySelector(`#${this.escapeSelector(nextLabel.htmlFor)}`);

      if (!nextInput || nextInput.disabled) return;

      nextInput.checked = true;
      nextInput.dispatchEvent(new Event("change", {bubbles: true}));
      nextLabel.scrollIntoView({behavior: "smooth", block: "nearest", inline: "center"});
      event.preventDefault();
    }

    getKeyboardDirection(key) {
      if (key === "ArrowRight" || key === "ArrowDown") return 1;
      if (key === "ArrowLeft" || key === "ArrowUp") return -1;

      return 0;
    }

    getLabels(input) {
      return Array.from(input.querySelectorAll("label[for]"));
    }

    scrollCheckedIntoView(input, behavior) {
      const checkedInput = input.querySelector("input[type='radio']:checked");
      const checkedLabel = checkedInput
        ? input.querySelector(`label[for="${this.escapeAttribute(checkedInput.id)}"]`)
        : null;

      checkedLabel?.scrollIntoView({
        behavior,
        block: "nearest",
        inline: "center",
      });
    }

    hasOverflow(input) {
      return input.scrollWidth > input.clientWidth + 1;
    }

    escapeSelector(value) {
      if (window.CSS?.escape) return CSS.escape(value);

      return value.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
    }

    escapeAttribute(value) {
      return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    }
  }

  customElements.define("product-pill-slider", ProductPillSlider);
}
